import React from 'react';

import { InfoPage, FeaturesPage } from '@vlabs/pages/app';
import { ImageChecksPage } from '@vlabs/pages/checks';
import { ChecksPage } from '@vlabs/pages/checks/ChecksPage';
import { VideoChecksResultPage } from '@vlabs/pages/checks/pages/video-checks/VideoChecksResultPage';
import { VideoAndAudioResultPage } from '@vlabs/pages/checks/results-page/VideoAndAudioResultsPage';
import { EditStreamPage as LunaStreamsEditStreamPage } from '@vlabs/pages/editStream/EditStreamPage';
import EventPage from '@vlabs/pages/events/event-page/EventPage';
import EventListPage from '@vlabs/pages/events/events/EventListPage';
import LatestEventListPage from '@vlabs/pages/events/latest-events/LatestEventListPage';
import { ExternalIdPage } from '@vlabs/pages/external-id';
import FacePage from '@vlabs/pages/face-details-page/FacePage';
import FacesPage from '@vlabs/pages/faces/FacesPage';
import CreateHandlerPage from '@vlabs/pages/handlers/CreateHandlerPage';
import { EditHandlerPage } from '@vlabs/pages/handlers/EditHandlerPage';
import HandlerListPage from '@vlabs/pages/handlers/HandlerListPage';
import OtherScenarioStepper from '@vlabs/pages/handlers/preconfigured-scenarios/other/OtherScenarioStepper';
import FaceListPage from '@vlabs/pages/lists/face-list-page/FaceListPage';
import ListsListPage from '@vlabs/pages/lists/ListsListPage';
import { PluginsPage } from '@vlabs/pages/plugin-list';
import { SearchPage } from '@vlabs/pages/search/SearchContainer';
import SourceListPage from '@vlabs/pages/sources/SourceListPage';
import { ChartSettings } from '@vlabs/pages/statistic/ChartSettings';
import { StatisticPage } from '@vlabs/pages/statistic/StatisticPage';
import { StreamPage as LunaStreamsStreamPage } from '@vlabs/pages/stream/StreamPage';
import TaskListPage from '@vlabs/pages/tasks/TaskListPage';
import CreateVerifier from '@vlabs/pages/verifiers/CreateVerifier';
import VerifierFormPage from '@vlabs/pages/verifiers/VerifierFormPage';
import VerifierListPage from '@vlabs/pages/verifiers/VerifierListPage';
import VerifierTestPage from '@vlabs/pages/verifiers/VerifierTestPage';
import { permissions } from '@vlabs/shared/config';
import withControl from '@vlabs/shared/hocs/routeControllerHOC';
import { selectAppServiceState } from '@vlabs/shared/selectors/appSelectors';
import { Pages } from '@vlabs/uikit';
import {
  Route, Redirect, Switch, generatePath,
} from 'react-router-dom';

import { selectEventsState } from '@vlabs/pages/events/events/selectors';
import { fetchEvents } from '@vlabs/pages/events/events/store';
import { selectLatestEventsState } from '@vlabs/pages/events/latest-events/selectors';
import { fetchLatestEvents } from '@vlabs/pages/events/latest-events/store';
import { selectHandlerStateById } from '@vlabs/pages/handlers/selectors';
import { fetchHandlerList } from '@vlabs/pages/handlers/store';

import { routes } from '@vlapps/luna/src/app/routes';
import AccountListPage from '@vlapps/luna/src/features/accounts/AccountListPage.tsx';

import { AuthTokenPage } from '../pages/auth-token/AuthTokenPage';

const includedAttributes = ['gender', 'age', 'temperature', 'liveness', 'deepfake', 'apparentGender', 'apparentAge', 'upperBody', 'lowerBody', 'headwear', 'backpack'];

export default function () {
  return (
    <Switch>

      <Route
        component={AuthTokenPage}
        exact
        path={routes.auth.token}
      />

      {/* [App] Monitoring */}
      <Route
        component={InfoPage}
        exact
        path={routes.app.info}
      />

      <Route
        component={withControl({
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(StatisticPage)}
        exact
        path={routes.events.statistic.all}
      />

      <Route
        component={withControl({
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(ChartSettings)}
        exact
        path={routes.events.statistic.read}
      />

      {/* [App] Features */}
      <Route
        component={FeaturesPage}
        exact
        path={routes.app.features}
      />

      {/* [App] Plugins */}
      <Route
        component={PluginsPage}
        exact
        path={routes.app.plugins}
      />

      <Route
        component={withControl({
          authRule: [permissions.account.view],
        })(AccountListPage)}
        path={routes.accounts.all}
      />

      {/* [Luna] latest events (ws) */}
      <Route
        component={withControl({
          authRule: [permissions.event.view],
          componentState: selectLatestEventsState,
          includedAttributes,
          onLoaded: ({ dispatch }) => dispatch(fetchLatestEvents),
        })(LatestEventListPage)}
        exact
        path={routes.events.latest}
      />

      {/* [Luna] events */}
      <Route
        component={withControl({
          authRule: [permissions.event.view],
          componentState: selectEventsState,
          includedAttributes,
          onLoaded: ({ dispatch }) => dispatch(fetchEvents),
        })(EventListPage)}
        exact
        path={routes.events.archive}
      />
      <Route
        component={withControl({
          authRule: [permissions.event.view],
        })(EventPage)}
        exact
        path={routes.events.eventDetails}
      />

      {/* [Luna] matching */}
      <Route
        component={withControl({
          authRule: [permissions.event.matching, permissions.face.matching],
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
          includedAttributes,
        })(SearchPage)}
        path={routes.search}
      />

      {/* [Luna] event sources */}
      <Route
        component={withControl({
          componentState: (state) => selectAppServiceState(state, 'lunaStreams'),
        })(SourceListPage)}
        exact
        path={routes.sources.main}
      />

      {/* [Luna streams] */}
      <Route
        exact
        path={routes.sources.lunaStreams.streams.create}
        render={(router) => {
          const Component = withControl({
            authRule: [permissions.source.creation],
            componentState: (state) => selectAppServiceState(state, 'lunaStreams'),
          })(LunaStreamsEditStreamPage);
          return (
            <Component
              afterCreateStream={(streamId) => {
                router.history.replace(generatePath(routes.sources.lunaStreams.streams.view, { streamId }));
              }}
              afterDeleteStream={() => {
                router.history.push(routes.sources.main);
              }}
              streamId="create"
              {...router}
            />
          );
        }}
      />
      <Route
        path={routes.sources.lunaStreams.streams.edit}
        render={(router) => {
          const Component = withControl({
            authRule: [permissions.source.modification],
            componentState: (state) => selectAppServiceState(state, 'lunaStreams'),
          })(LunaStreamsEditStreamPage);
          return (
            <Component
              afterCreateStream={(streamId) => {
                router.history.replace(generatePath(routes.sources.lunaStreams.streams.view, { streamId }));
              }}
              afterDeleteStream={() => {
                router.history.push(routes.sources.main);
              }}
              streamId={router.match.params.streamId}
              {...router}
            />
          );
        }}
      />
      <Route
        path={routes.sources.lunaStreams.streams.view}
        render={(router) => {
          const Component = withControl({
            componentState: (state) => selectAppServiceState(state, 'lunaStreams'),
          })(LunaStreamsStreamPage);
          return (
            <Component
              streamId={router.match.params.streamId}
              {...router}
            />
          );
        }}
      />

      {/* [Luna] handlers */}
      <Route
        component={withControl({
          authRule: [permissions.handler.creation],
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(OtherScenarioStepper)}
        exact
        path={routes.handlers.other}
      />
      <Route
        component={withControl({
          authRule: [permissions.handler.creation],
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(CreateHandlerPage)}
        path={routes.handlers.create}
      />
      <Route
        component={withControl({
          authRule: [permissions.handler.view],
          componentState: (state, { match }) => (
            selectHandlerStateById(state, match?.params?.handlerId)
          ),
        })(EditHandlerPage)}
        path={routes.handlers.read}
      />
      <Route
        component={withControl({
          authRule: [permissions.handler.view],
          onInit: ({ dispatch }) => dispatch(fetchHandlerList),
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(HandlerListPage)}
        exact
        path={routes.handlers.all}
      />

      {/* [Luna] verifiers */}
      <Route
        component={withControl({
          authRule: [permissions.verifier.view],
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(VerifierTestPage)}
        exact
        path={routes.verifiers.verify}
      />
      <Route
        component={withControl({
          authRule: [permissions.verifier.creation],
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(CreateVerifier)}
        path={routes.verifiers.create}
      />
      <Route
        component={withControl({
          authRule: [permissions.verifier.view],
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(VerifierFormPage)}
        exact
        path={routes.verifiers.read}
      />
      <Route
        component={withControl({
          authRule: [permissions.verifier.view],
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(VerifierListPage)}
        exact
        path={routes.verifiers.all}
      />

      {/* [Luna] lists */}
      <Route
        component={withControl({
          authRule: [permissions.list.view],
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(FaceListPage)}
        path={routes.lists.read}
      />
      <Route
        component={withControl({
          authRule: [permissions.list.view],
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(ListsListPage)}
        exact
        path={routes.lists.all}
      />

      {/* [Luna] faces */}
      <Route
        component={withControl({
          authRule: [permissions.face.view],
        })(FacePage)}
        path={routes.facePage}
      />
      <Route
        component={withControl({
          authRule: [permissions.face.view],
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(ExternalIdPage)}
        path={routes.externalId.read}
      />
      <Route
        component={withControl({
          authRule: [permissions.face.view],
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(FacesPage)}
        path={routes.faces.all}
      />

      {/* [Luna] tasks */}
      <Route
        component={withControl({
          authRule: [permissions.task.view],
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(TaskListPage)}
        path={routes.tasks.all}
      />

      {/* [Luna] checks */}
      <Route
        component={withControl({
          authRule: [permissions.resources.iso, permissions.resources.sdk, permissions.emit_events.allowed],
        })(ChecksPage)}
        exact
        path={routes.checks.read}
      />
      <Route
        component={ImageChecksPage}
        path={routes.checks.imageChecks}
      />
      <Route
        component={withControl({
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(VideoAndAudioResultPage)}
        path={routes.checks.videoAndAudioResults}
      />
      <Route
      // component={withControl({
      //   componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
      // })(VideoChecksResultPage)}
        component={VideoChecksResultPage}
        exact
        path={routes.checks.videoResults}
      />

      {/* Router settings */}
      <Route
        exact
        path="/"
        render={() => <Redirect to={routes.events.latest} />}
      />
      <Route
        component={Pages.NotFoundPage}
        path="*"
      />
    </Switch>
  );
}
